import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { Asset, FolderNode, AssetFilter } from '../../core/types/asset.types';
import {
  writeFileAsArrayBuffer,
  hasProjectRoot,
} from '../services/FileSystemService';
import { thumbnailCache } from '../utils/thumbnail-cache';
import { useConsoleStore } from './consoleStore';
import {
  getUniqueFileName,
  isImageFile,
  validateFileForImport,
  getFileExtension,
  extractFolderPaths,
  type FileImportResult,
  type DroppedFileEntry,
} from '../utils/file-import-helpers';

export type ViewMode = 'grid' | 'list';

interface ClipboardState {
  items: string[];  // asset or folder IDs
  operation: 'cut' | 'copy' | null;
  sourceFolderId: string | null;
}

interface ProjectPanelState {
  viewMode: ViewMode;
  gridSize: 'small' | 'medium' | 'large';
  showPreview: boolean;
  selectedAssetId: string | null;
  selectedAssetIds: string[];
  selectedFolderId: string;
  filter: AssetFilter;
  folders: Record<string, FolderNode>;
  assets: Record<string, Asset>;
  rootFolderId: string;
  draggedAssetId: string | null;
  dropTargetId: string | null;
  externalDropTargetId: string | null;
  contextMenuPosition: { x: number; y: number } | null;
  contextMenuTargetId: string | null;
  isRefreshing: boolean;
  lastRefreshError: string | null;
  clipboard: ClipboardState;
}

export type CreateAssetType = 'script-js' | 'script-ts' | 'scene' | 'prefab';

interface ProjectPanelActions {
  setViewMode: (mode: ViewMode) => void;
  setGridSize: (size: 'small' | 'medium' | 'large') => void;
  togglePreview: () => void;
  selectAsset: (id: string, multiSelect?: boolean) => void;
  selectFolder: (id: string) => void;
  clearSelection: () => void;
  setSearch: (search: string) => void;
  setTypeFilter: (types: AssetFilter['types']) => void;
  setSortBy: (sortBy: AssetFilter['sortBy']) => void;
  toggleSortDirection: () => void;
  toggleFolderExpanded: (id: string) => void;
  renameAsset: (id: string, newName: string) => void;
  deleteAsset: (id: string) => Promise<void>;
  deleteSelectedItems: () => Promise<void>;
  duplicateAsset: (id: string) => void;
  moveAsset: (assetId: string, targetFolderId: string) => Promise<void>;
  createFolder: (parentId: string, name: string) => Promise<void>;
  renameFolder: (id: string, newName: string) => void;
  deleteFolder: (id: string) => Promise<void>;
  createAsset: (parentId: string, assetType: CreateAssetType, name: string) => Promise<void>;
  setDraggedAsset: (id: string | null) => void;
  setDropTarget: (id: string | null) => void;
  setExternalDropTarget: (id: string | null) => void;
  importExternalFiles: (files: File[], targetFolderId: string) => Promise<void>;
  importExternalEntries: (entries: DroppedFileEntry[], targetFolderId: string) => Promise<void>;
  showContextMenu: (x: number, y: number, targetId: string) => void;
  hideContextMenu: () => void;
  refreshAssets: () => void | Promise<void>;
  setFileTree: (folders: Record<string, FolderNode>, assets: Record<string, Asset>, rootFolderId: string) => void;
  cutItems: (ids: string[], sourceFolderId: string) => void;
  copyItems: (ids: string[], sourceFolderId: string) => void;
  pasteItems: (targetFolderId: string) => Promise<void>;
  clearClipboard: () => void;
}

const generateId = () => Math.random().toString(36).substring(2, 11);

/**
 * Get default content for a new asset based on its type
 */
function getDefaultAssetContent(assetType: string, name: string): string {
  // Remove extension from name for class/function names
  const baseName = name.replace(/\.[^/.]+$/, '');

  switch (assetType) {
    case 'script-ts':
      return `// ${baseName}\n\nexport class ${baseName} {\n  constructor() {\n    // Initialize\n  }\n}\n`;
    case 'script-js':
      return `// ${baseName}\n\nexport function ${baseName}() {\n  // Implementation\n}\n`;
    case 'scene':
      return JSON.stringify({ objects: [], settings: {} }, null, 2);
    case 'prefab':
      return JSON.stringify({ type: 'Container', children: [] }, null, 2);
    default:
      return '';
  }
}

// Helper to detect asset type from file extension
const getAssetTypeFromExtension = (extension: string): Asset['type'] => {
  const ext = extension.toLowerCase();

  // Images / Sprites
  if (['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'].includes(ext)) {
    return 'sprite';
  }

  // Audio - Music (longer formats typically used for music)
  if (['.mp3', '.ogg', '.flac', '.aac', '.m4a'].includes(ext)) {
    return 'audio-music';
  }

  // Audio - SFX (shorter formats typically used for sound effects)
  if (['.wav'].includes(ext)) {
    return 'audio-sfx';
  }

  // Scripts
  if (ext === '.js') {
    return 'script-js';
  }
  if (ext === '.ts' || ext === '.tsx') {
    return 'script-ts';
  }

  // Prefabs
  if (ext === '.prefab') {
    return 'prefab';
  }

  // Scenes
  if (ext === '.scene') {
    return 'scene';
  }

  // Fonts
  if (['.ttf', '.otf', '.woff', '.woff2'].includes(ext)) {
    return 'font';
  }

  // Spine animations
  if (ext === '.skel' || ext === '.spine' || ext === '.atlas') {
    return 'spine';
  }

  return 'unknown';
};

// Demo data
const createDemoData = () => {
  const now = new Date();
  const hourAgo = new Date(now.getTime() - 2 * 60 * 60 * 1000);
  const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const folders: Record<string, FolderNode> = {
    root: { id: 'root', name: 'Assets', path: 'Assets', parentId: null, children: ['sprites', 'audio', 'scripts', 'prefabs', 'scenes'], expanded: true, type: 'folder' },
    sprites: { id: 'sprites', name: 'sprites', path: 'Assets/sprites', parentId: 'root', children: ['sprites-chars', 'sprites-items'], expanded: true, type: 'folder' },
    'sprites-chars': { id: 'sprites-chars', name: 'characters', path: 'Assets/sprites/characters', parentId: 'sprites', children: [], expanded: false, type: 'folder' },
    'sprites-items': { id: 'sprites-items', name: 'items', path: 'Assets/sprites/items', parentId: 'sprites', children: [], expanded: false, type: 'folder' },
    audio: { id: 'audio', name: 'audio', path: 'Assets/audio', parentId: 'root', children: ['audio-music', 'audio-sfx'], expanded: true, type: 'folder' },
    'audio-music': { id: 'audio-music', name: 'music', path: 'Assets/audio/music', parentId: 'audio', children: [], expanded: false, type: 'folder' },
    'audio-sfx': { id: 'audio-sfx', name: 'sfx', path: 'Assets/audio/sfx', parentId: 'audio', children: [], expanded: false, type: 'folder' },
    scripts: { id: 'scripts', name: 'scripts', path: 'Assets/scripts', parentId: 'root', children: [], expanded: true, type: 'folder' },
    prefabs: { id: 'prefabs', name: 'prefabs', path: 'Assets/prefabs', parentId: 'root', children: [], expanded: true, type: 'folder' },
    scenes: { id: 'scenes', name: 'scenes', path: 'Assets/scenes', parentId: 'root', children: [], expanded: true, type: 'folder' },
  };

  const assets: Record<string, Asset> = {
    'asset-player': { id: 'asset-player', name: 'player.png', path: 'Assets/sprites/characters/player.png', type: 'sprite', extension: '.png', parentId: 'sprites-chars', metadata: { width: 256, height: 256, fileSize: 45000 }, createdAt: weekAgo, modifiedAt: hourAgo },
    'asset-enemy': { id: 'asset-enemy', name: 'enemy.png', path: 'Assets/sprites/characters/enemy.png', type: 'sprite', extension: '.png', parentId: 'sprites-chars', metadata: { width: 128, height: 128, fileSize: 38000 }, createdAt: weekAgo, modifiedAt: hourAgo },
    'asset-coin': { id: 'asset-coin', name: 'coin.png', path: 'Assets/sprites/items/coin.png', type: 'sprite', extension: '.png', parentId: 'sprites-items', metadata: { width: 64, height: 64, fileSize: 12000 }, createdAt: dayAgo, modifiedAt: dayAgo },
    'asset-heart': { id: 'asset-heart', name: 'heart.png', path: 'Assets/sprites/items/heart.png', type: 'sprite', extension: '.png', parentId: 'sprites-items', metadata: { width: 32, height: 32, fileSize: 8000 }, createdAt: dayAgo, modifiedAt: dayAgo },
    'asset-star': { id: 'asset-star', name: 'star.png', path: 'Assets/sprites/items/star.png', type: 'sprite', extension: '.png', parentId: 'sprites-items', metadata: { width: 48, height: 48, fileSize: 10000 }, createdAt: dayAgo, modifiedAt: dayAgo },
    'asset-theme': { id: 'asset-theme', name: 'theme.mp3', path: 'Assets/audio/music/theme.mp3', type: 'audio-music', extension: '.mp3', parentId: 'audio-music', metadata: { duration: 180, fileSize: 5200000 }, createdAt: weekAgo, modifiedAt: weekAgo },
    'asset-jump': { id: 'asset-jump', name: 'jump.wav', path: 'Assets/audio/sfx/jump.wav', type: 'audio-sfx', extension: '.wav', parentId: 'audio-sfx', metadata: { duration: 0.5, fileSize: 45000 }, createdAt: dayAgo, modifiedAt: dayAgo },
    'asset-collect': { id: 'asset-collect', name: 'collect.wav', path: 'Assets/audio/sfx/collect.wav', type: 'audio-sfx', extension: '.wav', parentId: 'audio-sfx', metadata: { duration: 0.3, fileSize: 28000 }, createdAt: dayAgo, modifiedAt: dayAgo },
    'asset-player-ts': { id: 'asset-player-ts', name: 'Player.ts', path: 'Assets/scripts/Player.ts', type: 'script-ts', extension: '.ts', parentId: 'scripts', metadata: { fileSize: 3500 }, createdAt: hourAgo, modifiedAt: hourAgo },
    'asset-enemy-js': { id: 'asset-enemy-js', name: 'Enemy.js', path: 'Assets/scripts/Enemy.js', type: 'script-js', extension: '.js', parentId: 'scripts', metadata: { fileSize: 2800 }, createdAt: dayAgo, modifiedAt: dayAgo },
    'asset-coin-prefab': { id: 'asset-coin-prefab', name: 'Coin.prefab', path: 'Assets/prefabs/Coin.prefab', type: 'prefab', extension: '.prefab', parentId: 'prefabs', metadata: { fileSize: 1200 }, createdAt: dayAgo, modifiedAt: dayAgo },
    'asset-main-scene': { id: 'asset-main-scene', name: 'Main.scene', path: 'Assets/scenes/Main.scene', type: 'scene', extension: '.scene', parentId: 'scenes', metadata: { fileSize: 8500 }, createdAt: hourAgo, modifiedAt: hourAgo },
    'asset-level1': { id: 'asset-level1', name: 'Level1.scene', path: 'Assets/scenes/Level1.scene', type: 'scene', extension: '.scene', parentId: 'scenes', metadata: { fileSize: 12000 }, createdAt: dayAgo, modifiedAt: dayAgo },
  };

  // Link assets to folders
  Object.values(assets).forEach((asset) => {
    if (asset.parentId && folders[asset.parentId]) {
      folders[asset.parentId].children.push(asset.id);
    }
  });

  return { folders, assets };
};

const { folders: initialFolders, assets: initialAssets } = createDemoData();

const defaultFilter: AssetFilter = {
  search: '',
  types: [],
  sortBy: 'name',
  sortDirection: 'asc',
};

type ProjectPanelStore = ProjectPanelState & ProjectPanelActions;

export const useProjectPanelStore = create<ProjectPanelStore>()(
  immer((set, get) => ({
      viewMode: 'grid' as ViewMode,
      gridSize: 'medium' as const,
      showPreview: true,
      selectedAssetId: null,
      selectedAssetIds: [],
      selectedFolderId: 'root',
      filter: defaultFilter,
      folders: initialFolders,
      assets: initialAssets,
      rootFolderId: 'root',
      draggedAssetId: null,
      dropTargetId: null,
      externalDropTargetId: null,
      contextMenuPosition: null,
      contextMenuTargetId: null,
      isRefreshing: false,
      lastRefreshError: null,
      clipboard: {
        items: [],
        operation: null,
        sourceFolderId: null,
      },

      setViewMode: (mode) => set((s) => { s.viewMode = mode; }),
      setGridSize: (size) => set((s) => { s.gridSize = size; }),
      togglePreview: () => set((s) => { s.showPreview = !s.showPreview; }),

      selectAsset: (id, multiSelect = false) => set((s) => {
        if (multiSelect) {
          if (s.selectedAssetIds.includes(id)) {
            s.selectedAssetIds = s.selectedAssetIds.filter((i) => i !== id);
            s.selectedAssetId = s.selectedAssetIds[0] || null;
          } else {
            s.selectedAssetIds.push(id);
            s.selectedAssetId = id;
          }
        } else {
          s.selectedAssetId = id;
          s.selectedAssetIds = [id];
        }
      }),

      selectFolder: (id) => set((s) => {
        s.selectedFolderId = id;
        s.selectedAssetId = null;
        s.selectedAssetIds = [];
      }),

      clearSelection: () => set((s) => {
        s.selectedAssetId = null;
        s.selectedAssetIds = [];
      }),

      setSearch: (search) => set((s) => { s.filter.search = search; }),
      setTypeFilter: (types) => set((s) => { s.filter.types = types; }),
      setSortBy: (sortBy) => set((s) => { s.filter.sortBy = sortBy; }),
      toggleSortDirection: () => set((s) => {
        s.filter.sortDirection = s.filter.sortDirection === 'asc' ? 'desc' : 'asc';
      }),

      toggleFolderExpanded: (id) => set((s) => {
        if (s.folders[id]) s.folders[id].expanded = !s.folders[id].expanded;
      }),

      renameAsset: (id, newName) => set((s) => {
        if (s.assets[id]) {
          const ext = s.assets[id].extension;
          s.assets[id].name = newName.endsWith(ext) ? newName : newName + ext;
          s.assets[id].modifiedAt = new Date();
        }
      }),

      deleteAsset: async (id) => {
        const asset = get().assets[id];
        if (!asset) return;

        // Delete from disk first
        try {
          const { deleteFile, hasProjectRoot } = await import('../services/FileSystemService');
          if (hasProjectRoot()) {
            const relPath = asset.path.startsWith('/') ? asset.path.slice(1) : asset.path;
            await deleteFile(relPath);
          }
        } catch (err) {
          console.error('[projectPanelStore] Failed to delete file on disk:', err);
          // Still remove from store even if disk delete fails
        }

        set((s) => {
          if (s.assets[id]?.parentId && s.folders[s.assets[id].parentId!]) {
            s.folders[s.assets[id].parentId!].children = s.folders[s.assets[id].parentId!].children.filter((c) => c !== id);
          }
          delete s.assets[id];
          if (s.selectedAssetId === id) s.selectedAssetId = null;
          s.selectedAssetIds = s.selectedAssetIds.filter((i) => i !== id);
        });
      },

      deleteSelectedItems: async () => {
        const { selectedAssetIds, selectedAssetId } = get();
        const ids = selectedAssetIds.length > 0 ? [...selectedAssetIds] : selectedAssetId ? [selectedAssetId] : [];
        if (ids.length === 0) return;

        const names = ids.map((id) => {
          const a = get().assets[id];
          return a ? a.name : id;
        }).join(', ');

        if (!confirm(`Delete ${ids.length} item${ids.length > 1 ? 's' : ''}?\n${names}`)) return;

        for (const id of ids) {
          await get().deleteAsset(id);
        }
      },

      duplicateAsset: (id) => set((s) => {
        const asset = s.assets[id];
        if (asset) {
          const newId = generateId();
          const newName = asset.name.replace(asset.extension, '') + '_copy' + asset.extension;
          const parentFolder = asset.parentId ? s.folders[asset.parentId] : null;
          const newPath = parentFolder ? `${parentFolder.path}/${newName}` : newName;
          s.assets[newId] = { ...asset, id: newId, name: newName, path: newPath, createdAt: new Date(), modifiedAt: new Date() };
          if (asset.parentId && s.folders[asset.parentId]) {
            s.folders[asset.parentId].children.push(newId);
          }
        }
      }),

      moveAsset: async (assetId, targetFolderId) => {
        const { assets, folders } = get();
        const asset = assets[assetId];
        const targetFolder = folders[targetFolderId];
        if (!asset || !targetFolder) return;
        // Don't move to the same folder
        if (asset.parentId === targetFolderId) return;
        // Also check if already a child of target
        if (targetFolder.children.includes(assetId)) return;

        const oldPath = asset.path;
        const fileName = asset.name;
        const newPath = `${targetFolder.path}/${fileName}`;

        // Don't move if paths resolve to the same location
        if (oldPath === newPath) return;

        // Move file on disk first
        try {
          const { moveFile, hasProjectRoot } = await import('../services/FileSystemService');
          if (hasProjectRoot()) {
            const relOld = oldPath.startsWith('/') ? oldPath.slice(1) : oldPath;
            const relNew = newPath.startsWith('/') ? newPath.slice(1) : newPath;
            await moveFile(relOld, relNew);
          }
        } catch (err) {
          console.error('[projectPanelStore] Failed to move file on disk:', err);
          return;
        }

        // Update store only after disk move succeeds
        set((s) => {
          if (s.assets[assetId].parentId && s.folders[s.assets[assetId].parentId!]) {
            s.folders[s.assets[assetId].parentId!].children = s.folders[s.assets[assetId].parentId!].children.filter((c) => c !== assetId);
          }
          s.folders[targetFolderId].children.push(assetId);
          s.assets[assetId].parentId = targetFolderId;
          s.assets[assetId].path = newPath;
          s.assets[assetId].modifiedAt = new Date();
        });
      },

      createFolder: async (parentId, name) => {
        const state = get();
        const parent = state.folders[parentId];
        if (!parent) return;

        const newPath = `${parent.path}/${name}`;

        // Write to disk first
        try {
          const { createDirectory, hasProjectRoot } = await import('../services/FileSystemService');
          if (hasProjectRoot()) {
            // Remove leading 'Assets/' or similar root prefix for relative path
            const relativePath = newPath.startsWith('/') ? newPath.slice(1) : newPath;
            await createDirectory(relativePath, true);
          }
        } catch (err) {
          console.error('[projectPanelStore] Failed to create folder on disk:', err);
          throw err;
        }

        // Only update store if disk write succeeds
        const newId = generateId();
        set((s) => {
          s.folders[newId] = { id: newId, name, path: newPath, parentId, children: [], expanded: false, type: 'folder' };
          s.folders[parentId].children.push(newId);
        });
      },

      renameFolder: (id, newName) => set((s) => {
        if (s.folders[id] && id !== 'root') {
          s.folders[id].name = newName;
        }
      }),

      deleteFolder: async (id) => {
        if (id === 'root') return;
        const folder = get().folders[id];
        if (!folder) return;

        // Delete from disk first
        try {
          const { deleteDirectory, hasProjectRoot } = await import('../services/FileSystemService');
          if (hasProjectRoot()) {
            const relPath = folder.path.startsWith('/') ? folder.path.slice(1) : folder.path;
            await deleteDirectory(relPath, true);
          }
        } catch (err) {
          console.error('[projectPanelStore] Failed to delete folder on disk:', err);
        }

        set((s) => {
          if (s.folders[id]?.parentId && s.folders[s.folders[id].parentId!]) {
            s.folders[s.folders[id].parentId!].children = s.folders[s.folders[id].parentId!].children.filter((c) => c !== id);
          }
          delete s.folders[id];
          if (s.selectedFolderId === id) s.selectedFolderId = 'root';
        });
      },

      createAsset: async (parentId, assetType, name) => {
        const state = get();
        const parent = state.folders[parentId];
        if (!parent) return;

        // Determine extension and type based on asset type
        let extension: string;
        let type: Asset['type'];
        switch (assetType) {
          case 'script-js':
            extension = '.js';
            type = 'script-js';
            break;
          case 'script-ts':
            extension = '.ts';
            type = 'script-ts';
            break;
          case 'scene':
            extension = '.scene';
            type = 'scene';
            break;
          case 'prefab':
            extension = '.prefab';
            type = 'prefab';
            break;
          default:
            return;
        }

        const finalName = name.endsWith(extension) ? name : name + extension;
        const filePath = `${parent.path}/${finalName}`;

        // Write to disk first
        try {
          const { writeFile, hasProjectRoot } = await import('../services/FileSystemService');
          if (hasProjectRoot()) {
            const relativePath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
            const defaultContent = getDefaultAssetContent(assetType, finalName);
            await writeFile(relativePath, defaultContent);
          }
        } catch (err) {
          console.error('[projectPanelStore] Failed to create asset on disk:', err);
          throw err;
        }

        // Only update store if disk write succeeds
        const newId = generateId();
        const now = new Date();
        const newAsset: Asset = {
          id: newId,
          name: finalName,
          path: filePath,
          type,
          extension,
          parentId,
          metadata: { fileSize: 0 },
          createdAt: now,
          modifiedAt: now,
        };

        set((s) => {
          s.assets[newId] = newAsset;
          s.folders[parentId].children.push(newId);
          s.selectedAssetId = newId;
          s.selectedAssetIds = [newId];
        });
      },

      setDraggedAsset: (id) => set((s) => { s.draggedAssetId = id; }),
      setDropTarget: (id) => set((s) => { s.dropTargetId = id; }),
      setExternalDropTarget: (id) => set((s) => { s.externalDropTargetId = id; }),

      importExternalFiles: async (files, targetFolderId) => {
        const state = get();
        const targetFolder = state.folders[targetFolderId];

        if (!targetFolder) {
          console.error('[importExternalFiles] Target folder not found:', targetFolderId);
          return;
        }

        if (!hasProjectRoot()) {
          console.warn('[importExternalFiles] No project root set');
          useConsoleStore.getState().addMessage(
            'warning',
            'Cannot import files: No project is open',
            { source: 'system' }
          );
          return;
        }

        const results: FileImportResult[] = [];
        const consoleStore = useConsoleStore.getState();
        const totalFiles = files.length;
        let successCount = 0;
        let errorCount = 0;

        consoleStore.addMessage(
          'info',
          `Importing ${totalFiles} file${totalFiles === 1 ? '' : 's'} to ${targetFolder.name}...`,
          { source: 'system' }
        );

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const result: FileImportResult = {
            originalName: file.name,
            finalName: file.name,
            success: false,
          };

          try {
            // Validate file
            const validationError = validateFileForImport(file);
            if (validationError) {
              result.error = validationError;
              errorCount++;
              consoleStore.addMessage('warning', validationError, { source: 'system' });
              results.push(result);
              continue;
            }

            // Get unique file name
            const uniqueFileName = await getUniqueFileName(targetFolder.path, file.name);
            result.finalName = uniqueFileName;

            // Build path
            const relativePath = targetFolder.path
              ? `${targetFolder.path}/${uniqueFileName}`
              : uniqueFileName;

            const normalizedPath = relativePath.startsWith('/')
              ? relativePath.slice(1)
              : relativePath;

            // Read and write file
            const arrayBuffer = await file.arrayBuffer();
            await writeFileAsArrayBuffer(normalizedPath, arrayBuffer);

            result.success = true;
            result.path = relativePath;
            successCount++;

            // Log renamed files
            if (uniqueFileName !== file.name) {
              consoleStore.addMessage(
                'info',
                `Renamed "${file.name}" to "${uniqueFileName}" (file already existed)`,
                { source: 'system' }
              );
            }

            // Create asset entry
            const newId = generateId();
            const now = new Date();
            const extension = getFileExtension(uniqueFileName);
            const assetType = getAssetTypeFromExtension(extension);

            const newAsset: Asset = {
              id: newId,
              name: uniqueFileName,
              path: relativePath,
              type: assetType,
              extension,
              parentId: targetFolderId,
              metadata: {
                fileSize: file.size,
                mimeType: file.type || undefined,
              },
              createdAt: now,
              modifiedAt: now,
            };

            set((s) => {
              s.assets[newId] = newAsset;
              s.folders[targetFolderId].children.push(newId);
            });

            // Generate thumbnail for images
            if (isImageFile(uniqueFileName)) {
              setTimeout(() => {
                thumbnailCache
                  .getThumbnail(relativePath)
                  .catch((err) => {
                    console.warn(`Failed to generate thumbnail for ${uniqueFileName}:`, err);
                  });
              }, 100);
            }

          } catch (error) {
            result.error = error instanceof Error ? error.message : 'Unknown error';
            errorCount++;
            consoleStore.addMessage(
              'error',
              `Failed to import "${file.name}": ${result.error}`,
              { source: 'system' }
            );
          }

          results.push(result);
        }

        // Clear drop target
        set((s) => { s.externalDropTargetId = null; });

        // Log summary
        if (successCount > 0 && errorCount === 0) {
          consoleStore.addMessage(
            'info',
            `Successfully imported ${successCount} file${successCount === 1 ? '' : 's'}`,
            { source: 'system' }
          );
        } else if (successCount > 0 && errorCount > 0) {
          consoleStore.addMessage(
            'warning',
            `Imported ${successCount} file${successCount === 1 ? '' : 's'}, ${errorCount} failed`,
            { source: 'system' }
          );
        } else if (errorCount > 0) {
          consoleStore.addMessage(
            'error',
            `Failed to import ${errorCount} file${errorCount === 1 ? '' : 's'}`,
            { source: 'system' }
          );
        }
      },

      importExternalEntries: async (entries, targetFolderId) => {
        const state = get();
        const targetFolder = state.folders[targetFolderId];

        if (!targetFolder) {
          console.error('[importExternalEntries] Target folder not found:', targetFolderId);
          return;
        }

        if (!hasProjectRoot()) {
          console.warn('[importExternalEntries] No project root set');
          useConsoleStore.getState().addMessage(
            'warning',
            'Cannot import files: No project is open',
            { source: 'system' }
          );
          return;
        }

        const consoleStore = useConsoleStore.getState();
        const totalFiles = entries.length;

        if (totalFiles === 0) {
          return;
        }

        // Extract unique folder paths and count root-level items for logging
        const folderPaths = extractFolderPaths(entries);
        const rootItems = new Set(entries.map(e => e.relativePath.split('/')[0]));
        const hasMultipleItems = rootItems.size > 1 || folderPaths.size > 0;

        consoleStore.addMessage(
          'info',
          hasMultipleItems
            ? `Importing ${rootItems.size} item${rootItems.size === 1 ? '' : 's'} (${totalFiles} files) to ${targetFolder.name}...`
            : `Importing ${totalFiles} file${totalFiles === 1 ? '' : 's'} to ${targetFolder.name}...`,
          { source: 'system' }
        );

        let successCount = 0;
        let errorCount = 0;

        // First, create all necessary folders
        const { createDirectory } = await import('../services/FileSystemService');
        const createdFolders = new Map<string, string>(); // relativePath -> folderId

        // Sort folder paths by depth (shortest first) to create parents before children
        const sortedFolderPaths = Array.from(folderPaths).sort(
          (a, b) => a.split('/').length - b.split('/').length
        );

        for (const folderRelPath of sortedFolderPaths) {
          try {
            const fullPath = targetFolder.path
              ? `${targetFolder.path}/${folderRelPath}`
              : folderRelPath;

            const normalizedPath = fullPath.startsWith('/')
              ? fullPath.slice(1)
              : fullPath;

            await createDirectory(normalizedPath, true);

            // Create folder entry in store
            const parts = folderRelPath.split('/');
            const folderName = parts[parts.length - 1];

            // Determine parent folder ID
            let parentId = targetFolderId;
            if (parts.length > 1) {
              const parentRelPath = parts.slice(0, -1).join('/');
              parentId = createdFolders.get(parentRelPath) || targetFolderId;
            }

            // Check if folder already exists in store
            const existingFolder = Object.values(state.folders).find(
              f => f.path === fullPath
            );

            if (existingFolder) {
              createdFolders.set(folderRelPath, existingFolder.id);
            } else {
              const newFolderId = generateId();
              createdFolders.set(folderRelPath, newFolderId);

              set((s) => {
                s.folders[newFolderId] = {
                  id: newFolderId,
                  name: folderName,
                  path: fullPath,
                  parentId,
                  children: [],
                  expanded: true,
                  type: 'folder',
                };
                if (s.folders[parentId]) {
                  s.folders[parentId].children.push(newFolderId);
                }
              });
            }
          } catch (err) {
            console.error(`[importExternalEntries] Failed to create folder "${folderRelPath}":`, err);
            consoleStore.addMessage(
              'error',
              `Failed to create folder "${folderRelPath}": ${err instanceof Error ? err.message : 'Unknown error'}`,
              { source: 'system' }
            );
          }
        }

        // Now import all files
        for (const entry of entries) {
          try {
            // Validate file
            const validationError = validateFileForImport(entry.file);
            if (validationError) {
              errorCount++;
              consoleStore.addMessage('warning', validationError, { source: 'system' });
              continue;
            }

            // Determine target folder for this file
            const pathParts = entry.relativePath.split('/');
            const fileName = pathParts.pop()!;
            const fileParentRelPath = pathParts.join('/');

            // Get the correct parent folder ID
            let parentFolderId = targetFolderId;
            if (fileParentRelPath) {
              parentFolderId = createdFolders.get(fileParentRelPath) || targetFolderId;
            }

            // Get the parent folder's path
            const currentState = get();
            const parentFolder = currentState.folders[parentFolderId];
            const parentPath = parentFolder?.path || targetFolder.path;

            // Get unique file name
            const uniqueFileName = await getUniqueFileName(parentPath, fileName);

            // Build full path
            const relativePath = parentPath
              ? `${parentPath}/${uniqueFileName}`
              : uniqueFileName;

            const normalizedPath = relativePath.startsWith('/')
              ? relativePath.slice(1)
              : relativePath;

            // Read and write file
            const arrayBuffer = await entry.file.arrayBuffer();
            await writeFileAsArrayBuffer(normalizedPath, arrayBuffer);

            successCount++;

            // Log renamed files
            if (uniqueFileName !== fileName) {
              consoleStore.addMessage(
                'info',
                `Renamed "${fileName}" to "${uniqueFileName}" (file already existed)`,
                { source: 'system' }
              );
            }

            // Create asset entry
            const newId = generateId();
            const now = new Date();
            const extension = getFileExtension(uniqueFileName);
            const assetType = getAssetTypeFromExtension(extension);

            const newAsset: Asset = {
              id: newId,
              name: uniqueFileName,
              path: relativePath,
              type: assetType,
              extension,
              parentId: parentFolderId,
              metadata: {
                fileSize: entry.file.size,
                mimeType: entry.file.type || undefined,
              },
              createdAt: now,
              modifiedAt: now,
            };

            set((s) => {
              s.assets[newId] = newAsset;
              if (s.folders[parentFolderId]) {
                s.folders[parentFolderId].children.push(newId);
              }
            });

            // Generate thumbnail for images
            if (isImageFile(uniqueFileName)) {
              setTimeout(() => {
                thumbnailCache
                  .getThumbnail(relativePath)
                  .catch((err) => {
                    console.warn(`Failed to generate thumbnail for ${uniqueFileName}:`, err);
                  });
              }, 100);
            }

          } catch (error) {
            errorCount++;
            consoleStore.addMessage(
              'error',
              `Failed to import "${entry.relativePath}": ${error instanceof Error ? error.message : 'Unknown error'}`,
              { source: 'system' }
            );
          }
        }

        // Clear drop target
        set((s) => { s.externalDropTargetId = null; });

        // Log summary
        if (successCount > 0 && errorCount === 0) {
          consoleStore.addMessage(
            'info',
            `Successfully imported ${successCount} file${successCount === 1 ? '' : 's'}`,
            { source: 'system' }
          );
        } else if (successCount > 0 && errorCount > 0) {
          consoleStore.addMessage(
            'warning',
            `Imported ${successCount} file${successCount === 1 ? '' : 's'}, ${errorCount} failed`,
            { source: 'system' }
          );
        } else if (errorCount > 0) {
          consoleStore.addMessage(
            'error',
            `Failed to import ${errorCount} file${errorCount === 1 ? '' : 's'}`,
            { source: 'system' }
          );
        }
      },

      showContextMenu: (x, y, targetId) => set((s) => {
        s.contextMenuPosition = { x, y };
        s.contextMenuTargetId = targetId;
      }),

      hideContextMenu: () => set((s) => {
        s.contextMenuPosition = null;
        s.contextMenuTargetId = null;
      }),

      refreshAssets: async () => {
        // Set loading state
        set((s) => {
          s.isRefreshing = true;
          s.lastRefreshError = null;
        });

        try {
          const { getProjectRoot } = await import('../services/FileSystemService');
          const projectRoot = getProjectRoot();

          if (projectRoot) {
            const { refreshProjectFiles } = await import('../utils/project-files');
            await refreshProjectFiles();
            set((s) => { s.isRefreshing = false; });
            return;
          }

          // Fall back to demo data if no project is open
          const { folders, assets } = createDemoData();
          set((s) => {
            s.folders = folders;
            s.assets = assets;
            s.isRefreshing = false;
          });
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';
          console.warn('[projectPanelStore] Failed to refresh:', err);

          const { folders, assets } = createDemoData();
          set((s) => {
            s.folders = folders;
            s.assets = assets;
            s.isRefreshing = false;
            s.lastRefreshError = errorMessage;
          });
        }
      },

      setFileTree: (folders, assets, rootFolderId) => set((s) => {
        // Preserve expanded state from previous folders
        const prevFolders = s.folders;
        for (const id of Object.keys(folders)) {
          if (prevFolders[id] && prevFolders[id].expanded) {
            folders[id].expanded = true;
          }
        }
        s.folders = folders;
        s.assets = assets;
        s.rootFolderId = rootFolderId;
        // Preserve selected folder if it still exists, otherwise reset to root
        if (!folders[s.selectedFolderId]) {
          s.selectedFolderId = rootFolderId;
        }
        // Preserve selected assets if they still exist
        if (s.selectedAssetId && !assets[s.selectedAssetId]) {
          s.selectedAssetId = null;
        }
        s.selectedAssetIds = s.selectedAssetIds.filter((id) => assets[id]);
      }),

      cutItems: (ids, sourceFolderId) => {
        set((s) => {
          s.clipboard = { items: ids, operation: 'cut', sourceFolderId };
        });
      },

      copyItems: (ids, sourceFolderId) => {
        set((s) => {
          s.clipboard = { items: ids, operation: 'copy', sourceFolderId };
        });
      },

      pasteItems: async (targetFolderId) => {
        const { clipboard, assets, folders } = get();
        if (!clipboard.items.length || !clipboard.operation) return;

        const targetFolder = folders[targetFolderId];
        if (!targetFolder) return;

        for (const itemId of clipboard.items) {
          const asset = assets[itemId];
          const folder = folders[itemId];

          if (clipboard.operation === 'cut') {
            // Move the item
            if (asset) {
              await get().moveAsset(itemId, targetFolderId);
            } else if (folder && folder.id !== 'root') {
              // Move folder (update parentId and children arrays)
              set((s) => {
                const oldParentId = s.folders[itemId]?.parentId;
                if (oldParentId && s.folders[oldParentId]) {
                  s.folders[oldParentId].children = s.folders[oldParentId].children.filter(c => c !== itemId);
                }
                if (s.folders[itemId]) {
                  s.folders[itemId].parentId = targetFolderId;
                  // Update path
                  const newPath = `${targetFolder.path}/${s.folders[itemId].name}`;
                  s.folders[itemId].path = newPath;
                }
                s.folders[targetFolderId].children.push(itemId);
              });
            }
          } else if (clipboard.operation === 'copy') {
            // Duplicate the item
            if (asset) {
              const newId = generateId();
              const newName = asset.name.replace(asset.extension, '') + '_copy' + asset.extension;
              const newPath = `${targetFolder.path}/${newName}`;
              set((s) => {
                s.assets[newId] = {
                  ...asset,
                  id: newId,
                  name: newName,
                  path: newPath,
                  parentId: targetFolderId,
                  createdAt: new Date(),
                  modifiedAt: new Date(),
                };
                s.folders[targetFolderId].children.push(newId);
              });
            }
            // Note: Folder copy would require recursive copy, keeping it simple for now
          }
        }

        // Clear clipboard after cut (not after copy)
        if (clipboard.operation === 'cut') {
          set((s) => {
            s.clipboard = { items: [], operation: null, sourceFolderId: null };
          });
        }
      },

      clearClipboard: () => {
        set((s) => {
          s.clipboard = { items: [], operation: null, sourceFolderId: null };
        });
      },

      // Project state methods
      loadProjectState: (state: { expandedFolders?: string[]; selectedAssetPath?: string | null; selectedFolderId?: string; viewMode?: ViewMode }) => {
        set((s) => {
          // Restore expanded folders
          if (state.expandedFolders) {
            for (const folderId of state.expandedFolders) {
              if (s.folders[folderId]) {
                s.folders[folderId].expanded = true;
              }
            }
          }
          s.selectedFolderId = state.selectedFolderId ?? 'root';
          s.viewMode = state.viewMode ?? 'grid';
          // Note: selectedAssetPath would need lookup to find asset ID
        });
      },

      getProjectState: () => {
        const state = get();
        // Collect expanded folder IDs
        const expandedFolders = Object.values(state.folders)
          .filter((f) => f.expanded)
          .map((f) => f.id);
        return {
          expandedFolders,
          selectedFolderId: state.selectedFolderId,
          selectedAssetPath: state.selectedAssetId ? state.assets[state.selectedAssetId]?.path : null,
          viewMode: state.viewMode ?? 'grid',
        };
      },

      resetProjectState: () => {
        set((s) => {
          // Reset to empty state (no files shown when no project open)
          const emptyRoot: FolderNode = {
            id: 'root',
            name: 'No Project',
            path: '',
            parentId: null,
            children: [],
            expanded: true,
            type: 'folder',
          };
          s.folders = { root: emptyRoot };
          s.assets = {};
          s.rootFolderId = 'root';
          s.selectedFolderId = 'root';
          s.selectedAssetId = null;
          s.selectedAssetIds = [];
        });
      },
    }))
);

export const useFilteredAssets = () => {
  const { filter, selectedFolderId, assets, folders } = useProjectPanelStore();

  const collectAssets = (folderId: string): Asset[] => {
    const folder = folders[folderId];
    if (!folder) return [];
    // Only return direct children assets (not recursive)
    return folder.children.filter((id) => assets[id]).map((id) => assets[id]);
  };

  let filtered = collectAssets(selectedFolderId);

  if (filter.search) {
    const search = filter.search.toLowerCase();
    filtered = filtered.filter((a) => a.name.toLowerCase().includes(search));
  }

  if (filter.types.length > 0) {
    filtered = filtered.filter((a) => filter.types.includes(a.type));
  }

  filtered.sort((a, b) => {
    let cmp = 0;
    switch (filter.sortBy) {
      case 'name': cmp = a.name.localeCompare(b.name); break;
      case 'type': cmp = a.type.localeCompare(b.type); break;
      case 'size': cmp = (a.metadata?.fileSize || 0) - (b.metadata?.fileSize || 0); break;
      case 'modified': cmp = new Date(a.modifiedAt).getTime() - new Date(b.modifiedAt).getTime(); break;
    }
    return filter.sortDirection === 'asc' ? cmp : -cmp;
  });

  return filtered;
};

/** Returns immediate child folders of the selected folder, sorted by name */
export const useChildFolders = () => {
  const { selectedFolderId, folders } = useProjectPanelStore();
  const folder = folders[selectedFolderId];
  if (!folder) return [];
  return folder.children
    .filter((id) => folders[id])
    .map((id) => folders[id])
    .sort((a, b) => a.name.localeCompare(b.name));
};
